import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Card, Text } from '@lmn/design-system';
import { IconNoRefund, IconTrain } from '@lmn/lastminute-react-icons/lastminute-icons-solid.js';
import { IconMarker, IconMoon, IconPlaneNe, IconTravelFlexibly } from '@/app/common/icons/libraryIcons';
import Tooltip from '@/app/components/hotel-detail/Tooltip';

import { getSingleSource } from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { getNights, getTripDates } from '@/app/components/hotel-detail/common/dates';

import { useTranslation } from '@/app/components/translations/Translation';

import MealPlanIcon from '@/app/components/hotel-detail/MealPlanIcon';
import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';
import { pricePerNight } from '@/app/components/hotel-detail/common/common.helper';
import Stars from '@/app/common/components/Stars';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { ReviewsSummaryRecommendedHotelCard } from './ReviewsSummary';
import { PriceTypeLabel } from './PriceSummary';
import { getDoublePricingAmounts } from './rooms/roomCommon';

export type HotelCardProps = {
  category: number;
  checkin: string | undefined;
  checkout: string | undefined;
  city?: string;
  cityTaxes: number;
  flightIncluded?: boolean;
  image: string;
  isCancellable: boolean;
  isDoublePrice: boolean;
  isDoublePriceDP: boolean;
  isTopSecret: boolean;
  locale: string;
  mealPlanKey: string;
  name: string;
  onClick?: (event: React.SyntheticEvent) => void;
  passengers: number;
  price: number;
  priceType: PriceType;
  reviews?: number;
  reviewsScore?: number;
  reviewSource?: string;
  roomsIncludedInPrice: number;
  strikeThroughPrice: number;
  taxesIncludedInPrice: boolean;
  trainIncluded?: boolean;
  distanceText: string | undefined;
  hasLongPrice?: boolean;
} & React.HTMLAttributes<HTMLElement>;

// #region - Styles
const HotelCardStyled = styled(Card)(
  ({ theme }) => `
  position: relative;
  flex-direction: column;
  color: ${theme.commons.colors.gray800};
  display: flex;
  min-width: 322px;
  overflow: hidden;
  width: 322px;
   -webkit-transform: translate3d(0, 0, 0);
`
);

const CardImage = styled.div`
  height: 160px;
  overflow: hidden;
  position: relative;
  width: auto;
  -webkit-transform: translate3d(0, 0, 0);
`;

const HotelPicture = styled(Picture)`
  border-bottom-right-radius: 0.5rem;
  border-bottom-left-radius: 0.5rem;
  cursor: pointer;
  height: 160px;
  object-fit: cover;
  transform: scale3d(1, 1, 1);
  transition: transform 1s;
  width: 100%;
`;

const CardInfo = styled.div`
  flex-grow: 1;
  height: auto;
  padding: 12px 16px;
`;

const HotelDates = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  font-size: ${theme.commons.font.sizes.base};
  font-weight: ${theme.commons.font.weights.bold};
  line-height: ${theme.commons.font.heights.base};
  margin: 8px 0px;
`
);

const HotelName = styled.div(
  ({ theme }) => `
  font-size: ${theme.commons.font.sizes.large};
  font-weight: ${theme.commons.font.weights.bold};
  line-height: ${theme.commons.font.heights.large};
  margin-bottom: 0.15rem;
  max-width: 306px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
);

const HotelRating = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const HotelDetailList = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const HotelDetailLine = styled.div`
  align-items: center;
  display: flex;
  font-size: ${({ theme }) => theme.commons.font.sizes.base};
  font-weight: ${(p) => p.theme.commons.font.weights.regular};
  line-height: 26px;
`;

const iconStyle = css`
  font-size: ${({ theme }) => theme.commons.font.sizes.medium};
  margin-right: 0.25rem;
`;

const StyledIconPlaneNe = styled(IconPlaneNe)`
  ${iconStyle}
`;

const StyledIconTrain = styled(IconTrain)`
  ${iconStyle}
`;

const StyledMealPlanIcon = styled(MealPlanIcon)`
  ${iconStyle}
`;

const StyledFreeCancellationIcon = styled(IconTravelFlexibly)`
  ${iconStyle}
`;

const StyledNoRefundableIcon = styled(IconNoRefund)`
  ${iconStyle}
`;

const TopSecretOverlay = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 34px;
  background-color: ${({ theme }) => theme.commons.colors.tsh};
  text-align: right;
  z-index: 1;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
  clip-path: polygon(0 35%, 100% 0, 100% 100%, 0 100%);
`;

const TopSecretLogo = styled.img`
  height: 1.25rem;
  left: 145px;
  max-width: 300px;
  padding: 0 0 0 15px;
  position: absolute;
  bottom: 5px;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  padding-top: 16px;
`;

const PriceBadgesAndDiscount = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px 8px;
`;

const PriceAndDiscountContainer = styled.div`
  display: flex;
  gap: 4px 8px;
  flex-direction: row;
  align-items: baseline;
`;

const FlightPlusHotelLabel = styled.div`
  font-size: ${(p) => p.theme.commons.font.sizes.small};
  text-align: right;
`;

const RoomPriceContainer = styled.div`
  display: inline-flex;
`;

const RoomPrice = styled.div`
  color: ${(p) => p.theme.commons.colors.accent};
  font-size: ${(p) => p.theme.commons.font.sizes.xlarge};
  font-weight: ${(p) => p.theme.commons.font.weights.bold};
  line-height: 26px;
`;

const RoomPriceType = styled.span`
  color: ${(p) => p.theme.commons.colors.accent};
  margin-left: 4px;
  font-size: ${(p) => p.theme.commons.font.sizes.base};
  line-height: ${(p) => p.theme.commons.font.sizes.small};
  font-weight: ${(p) => p.theme.commons.font.weights.regular};

  &:empty {
    margin-left: 0;
  }
`;

const DiscountPrice = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StrikeThroughPrice = styled.span`
  position: relative;
  align-items: center;
  color: ${(p) => p.theme.commons.colors.gray};
  font-size: ${(p) => p.theme.commons.font.sizes.medium};
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 0 4px;

  &::before {
    top: 50%;
    background: ${(p) => p.theme.commons.colors.primary};
    content: '';
    position: absolute;
    height: 2px;
    left: 5%;
    width: 90%;
    white-space: nowrap;
    display: block;
  }
`;

const LocalTaxesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: flex-end;
  flex-direction: column;
  color: ${(p) => p.theme.commons.colors.gray400};
  margin: 0 !important;
`;

const DistanceTextWrap = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 12px;
  left: 12px;
`;

const DistanceText = styled.div`
  display: flex;
  align-items: center;
  border-radius: 0.25rem;
  background-color: ${(p) => p.theme.commons.colors.gray50};
  font-size: ${(p) => p.theme.commons.font.sizes.small};
  font-weight: ${(p) => p.theme.commons.font.weights.regular};
  line-height: 16px;
  padding: 2px 8px 2px 4px;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  min-height: 14px;
  margin-right: 8px;
`;

// #endregion

function calculatePriceFromType({
  priceType,
  price,
  guests,
  nights,
}: {
  priceType: string;
  price: number;
  guests?: number;
  nights?: number;
}) {
  if (priceType === 'PER_PERSON' && guests && price) {
    return price / guests;
  }
  if (priceType === 'PER_ROOM_NIGHT' && nights && price) {
    return price / nights;
  }
  return price;
}

const CardDates = ({ locale, checkin, checkout }: { locale: string; checkin: string; checkout: string }) => {
  if (!checkin || !checkout) return null;

  const formattedDates = getTripDates(locale, checkin, checkout);
  return (
    <>
      {formattedDates ? formattedDates.checkinCardFormatted : ''}&nbsp;-&nbsp;
      {formattedDates ? formattedDates.checkoutCardFormatted : ''}
    </>
  );
};

const RecommendedHotelCard = (props: HotelCardProps) => {
  const {
    category,
    checkin,
    checkout,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    city,
    cityTaxes: finalTotalCityTaxes,
    flightIncluded = false,
    image,
    isCancellable,
    isTopSecret,
    isDoublePrice,
    isDoublePriceDP,
    locale,
    mealPlanKey,
    name,
    onClick = () => null,
    passengers,
    price: totalPriceWithoutTaxes,
    priceType,
    reviewsScore,
    reviews,
    reviewSource,
    roomsIncludedInPrice,
    strikeThroughPrice: strikeThroughTotalPriceWithoutTaxes,
    taxesIncludedInPrice,
    trainIncluded,
    distanceText,
    hasLongPrice = false,
    ...rest
  } = props;

  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;

  const [showTooltip, setShowTooltip] = useState(false);
  const hotelNameElement = useRef<HTMLDivElement>(null);
  const containerElement = useRef<HTMLDivElement>(null);

  const tshImageSources = image
    ? [{ srcSet: getSingleSource(image, 'w_160,h_80,c_fill,q_auto,f_auto,e_blur:100') }]
    : [];
  const imageSources = image ? [{ srcSet: getSingleSource(image, 'w_323,h_160,c_fill,q_auto,f_auto') }] : [];

  const tshImageUrl = image ? getSingleSource(image, 'w_160,h_80,c_fill,q_auto,f_auto,e_blur:100') : '';
  const imageUrl = image ? getSingleSource(image, 'w_323,h_160,c_fill,q_auto,f_auto') : '';

  const tripNights = checkin && checkout ? getNights(checkin, checkout) : 1;

  const hotelOnly = !(flightIncluded || trainIncluded);
  const showFlightHotelLabel = !hotelOnly;

  const finalTotalPrice = taxesIncludedInPrice ? totalPriceWithoutTaxes + finalTotalCityTaxes : totalPriceWithoutTaxes;
  const finalPrice = isDoublePrice
    ? pricePerNight(finalTotalPrice, tripNights, roomsIncludedInPrice)
    : calculatePriceFromType({ priceType, price: finalTotalPrice, guests: passengers, nights: tripNights });

  const finalStrikeThroughTotalPrice = taxesIncludedInPrice
    ? strikeThroughTotalPriceWithoutTaxes + finalTotalCityTaxes
    : strikeThroughTotalPriceWithoutTaxes;
  const finalStrikeThroughPrice = isDoublePrice
    ? pricePerNight(finalStrikeThroughTotalPrice, tripNights, roomsIncludedInPrice)
    : calculatePriceFromType({
        priceType,
        price: finalStrikeThroughTotalPrice,
        guests: passengers,
        nights: tripNights,
      });

  const finalCityTaxes = calculatePriceFromType({
    priceType,
    price: finalTotalCityTaxes,
    guests: passengers,
    nights: tripNights,
  });

  const totalCityTaxesText = `+${formatPrice({ price: finalCityTaxes, localization })}`;

  const doublePriceMessage = isDoublePriceDP
    ? `${formatPrice({ price: finalTotalPrice, localization })} ${t('vacation.common.doubleprice.total_dp')}`
    : roomsIncludedInPrice > 1
      ? tripNights === 1
        ? t('vacation.select.room.price.includes.total.rooms.night', {
            price: formatPrice({ price: finalTotalPrice, localization }),
            rooms: roomsIncludedInPrice.toString(),
            nights: tripNights?.toString() || '',
          })
        : t('vacation.select.room.price.includes.total.rooms.nights', {
            price: formatPrice({ price: finalTotalPrice, localization }),
            rooms: roomsIncludedInPrice.toString(),
            nights: tripNights?.toString() || '',
          })
      : tripNights === 1
        ? t('vacation.select.room.price.includes.total.night', {
            nights: tripNights?.toString() || '',
          })
        : t('vacation.select.room.price.includes.total.room.nights', {
            price: formatPrice({ price: finalTotalPrice, localization }),
            nights: tripNights?.toString() || '',
          });

  const taxesIncludedPopup = localization?.configValuesApp?.taxesIncludedPopup;
  const taxesIncludedDoublePricing = localization?.configValuesApp?.taxesIncludedDoublePricing;

  const dpAmounts = taxesIncludedDoublePricing
    ? getDoublePricingAmounts({
        pricePerPerson: finalPrice,
        taxesPerPerson: finalCityTaxes,
        passengers,
        isDoublePriceDP,
      })
    : null;

  useEffect(() => {
    const containerWidth = containerElement.current?.scrollWidth ?? 0;
    const hotelNameWidth = hotelNameElement.current?.scrollWidth ?? 0;

    if (hotelNameWidth > containerWidth) {
      setShowTooltip(true);
    }
  }, []);

  return (
    <HotelCardStyled
      isClickable={true}
      isOutlined={false}
      fill="gray0"
      elevation="medium"
      radius="8px"
      padding={0}
      onClick={onClick}
      onKeyPress={onClick}
      {...rest}
    >
      <CardImage>
        <HotelPicture
          alt={isTopSecret ? 'top secret' : ''}
          url={isTopSecret ? tshImageUrl : imageUrl}
          key={image}
          sources={isTopSecret ? tshImageSources : imageSources}
          loading="lazy"
        />
        {!isTopSecret && distanceText && (
          <DistanceTextWrap>
            <DistanceText>
              <IconWrap>
                <IconMarker size={14} />
              </IconWrap>
              <span>{distanceText}</span>
            </DistanceText>
          </DistanceTextWrap>
        )}
        {isTopSecret && (
          <TopSecretOverlay>
            <TopSecretLogo src={`${imageBasePath}logo-tsh.svg`} alt="top secret logo" />
          </TopSecretOverlay>
        )}
      </CardImage>
      <CardInfo ref={containerElement}>
        {showTooltip ? (
          <Tooltip label={name}>
            <HotelName ref={hotelNameElement}>{name}</HotelName>
          </Tooltip>
        ) : (
          <HotelName ref={hotelNameElement}>{name}</HotelName>
        )}
        <HotelRating>
          <Stars stars={category} black={true} />
          {reviewsScore && reviews ? (
            <ReviewsSummaryRecommendedHotelCard
              reviewsScore={reviewsScore}
              reviews={reviews}
              reviewSource={reviewSource}
            />
          ) : null}
        </HotelRating>
        {checkin && checkout && (
          <HotelDates>
            <IconMoon
              style={{
                marginRight: '8px',
              }}
            />
            {tripNights === 1 ? `1 ${t('vacation.common.nights')}` : `${tripNights} ${t('vacation.common.nights')}`}
            <span
              css={css`
                color: ${(p) => p.theme.commons.colors.gray200};
                padding: 0 4px;
              `}
            >
              {'·'}
            </span>
            {checkin && checkout && <CardDates locale={locale} checkin={checkin} checkout={checkout} />}
          </HotelDates>
        )}

        <HotelDetailList>
          {isCancellable ? (
            <HotelDetailLine>
              <StyledFreeCancellationIcon />
              {hotelOnly
                ? t('vacation.common.mealplan.room.cancellationtype.refundable.title.ho')
                : t('vacation.common.mealplan.room.cancellationtype.refundable.title.dp')}
            </HotelDetailLine>
          ) : (
            <HotelDetailLine>
              <StyledNoRefundableIcon />
              {t('vacation.common.mealplan.room.cancellationtype.nonrefundable.title')}
            </HotelDetailLine>
          )}
          <HotelDetailLine>
            <StyledMealPlanIcon mealPlanType={mealPlanKey} /> {t(mealPlanKey)}
          </HotelDetailLine>
          {flightIncluded && (
            <HotelDetailLine>
              <StyledIconPlaneNe /> {t('vacation.select.hotelcard.includedtransport.flight')}
            </HotelDetailLine>
          )}
          {trainIncluded && (
            <HotelDetailLine>
              <StyledIconTrain /> {t('vacation.select.lastseenhotelcard.includedtransport.train')}
            </HotelDetailLine>
          )}
        </HotelDetailList>

        <PriceContainer>
          <PriceBadgesAndDiscount
            css={`
              ${hasLongPrice ? `flex-direction: column;` : `align-items: baseline;`}
            `}
          >
            {showFlightHotelLabel && (
              <FlightPlusHotelLabel>
                {t(trainIncluded ? 'vacation.common.trainplushotel' : 'vacation.common.flightplushotel').toUpperCase()}
              </FlightPlusHotelLabel>
            )}
            <PriceAndDiscountContainer>
              {hotelOnly && finalStrikeThroughPrice > finalPrice ? (
                <DiscountPrice
                  css={`
                    gap: 4px;
                  `}
                >
                  <StrikeThroughPrice>
                    {formatPrice({ price: finalStrikeThroughPrice, localization })}
                  </StrikeThroughPrice>
                </DiscountPrice>
              ) : null}
              <RoomPriceContainer>
                {hotelOnly ? (
                  <div
                    css={`
                      display: flex;
                      order: 1;
                      align-items: baseline;
                    `}
                  >
                    <span
                      css={`
                        padding-right: 4px;
                      `}
                    >
                      {t('vacation.common.price.from')}
                    </span>
                    <RoomPrice>{formatPrice({ price: finalPrice, localization })}</RoomPrice>
                    {roomsIncludedInPrice > 1 || (tripNights && tripNights > 1) ? (
                      <RoomPriceType>
                        <PriceTypeLabel priceType={priceType} roomsIncludedInPrice={roomsIncludedInPrice} />
                      </RoomPriceType>
                    ) : null}
                  </div>
                ) : (
                  <div
                    css={`
                      display: flex;
                      order: 1;
                      align-items: baseline;
                    `}
                  >
                    <RoomPrice>{formatPrice({ price: finalPrice, localization })}</RoomPrice>
                    <RoomPriceType>{t('vacation.select.room.price.dp.perperson.short')}</RoomPriceType>
                  </div>
                )}
              </RoomPriceContainer>
            </PriceAndDiscountContainer>
          </PriceBadgesAndDiscount>

          <LocalTaxesContainer {...rest}>
            {isDoublePrice || isDoublePriceDP ? (
              <div
                {...rest}
                css={css`
                  color: ${
                    isDoublePrice || isDoublePriceDP
                      ? (p) => p.theme.commons.colors.gray800
                      : (p) => p.theme.commons.colors.gray400
                  };
                  font-size: ${(p) => p.theme.commons.font.sizes.small};
                  font-weight: bold;
                  line-height: 1rem;
                  margin: 0 !important;
                `}
              >
                <span>{doublePriceMessage}</span>
              </div>
            ) : null}
            {finalTotalCityTaxes > 0 || ((taxesIncludedPopup || taxesIncludedDoublePricing) && taxesIncludedInPrice) ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                {taxesIncludedDoublePricing ? (
                  <span
                    css={css`
                      font-size: ${(p) => p.theme.commons.font.sizes.small};
                      text-align: right;
                      color: ${(p) => p.theme.commons.colors.gray400};

                      strong {
                        white-space: nowrap;
                      }
                    `}
                    dangerouslySetInnerHTML={{
                      __html: t('vacation.oss.cards.taxes_double_pricing.includes_label.short', {
                        amount: `<strong>${formatPrice({ price: dpAmounts!.total, localization })}</strong>${dpAmounts!.showPpSuffix ? ` ${t('vacation.select.room.price.dp.perperson.short')}` : ''}`,
                      }),
                    }}
                  />
                ) : (
                  <Text
                    variant="BodySmall"
                    css={`
                      text-align: right;
                    `}
                  >
                    {taxesIncludedPopup && taxesIncludedInPrice && !isDoublePriceDP
                      ? t('vacation.oss.cards.taxes_fees_included.all.title')
                      : (!taxesIncludedInPrice || isDoublePriceDP) && totalCityTaxesText
                        ? t('vacation.select.rooms.excludedcharges.long', { amount: totalCityTaxesText })
                        : taxesIncludedPopup
                          ? t('vacation.select.rooms.includedcharges.long.dp', { amount: totalCityTaxesText })
                          : t('vacation.common.citytaxes.nohiddenfees')}
                  </Text>
                )}
              </div>
            ) : null}
          </LocalTaxesContainer>
        </PriceContainer>
      </CardInfo>
    </HotelCardStyled>
  );
};

export default RecommendedHotelCard;
